import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { api } from "@/lib/api";
import { ShieldCheck, Lock, Link2, CheckCircle2, Smartphone, ChevronDown, Fingerprint, AlertTriangle, Share2 } from "lucide-react";

const LEVEL_STYLE = {
  "Mutually Witnessed": "bg-blue-50 text-blue-800 border-blue-300",
  Witnessed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Self-Declared": "bg-slate-100 text-slate-700 border-slate-300",
};

export default function ReceiptCard() {
  const { ownerToken } = useParams();
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/my-declaration/${ownerToken}`)
      .then((r) => setView(r.data))
      .catch(() => setError("This receipt link was not found."));
  }, [ownerToken]);

  if (error) {
    return <div className="wyv-container py-16"><Card className="max-w-md mx-auto"><CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</CardContent></Card></div>;
  }
  if (!view) {
    return <div className="wyv-container py-16 max-w-md mx-auto"><Skeleton className="h-96 rounded-2xl" /></div>;
  }

  const mutual = view.witness_status === "mutually_witnessed";
  const witnessed = ["witnessed", "mutually_witnessed"].includes(view.witness_status);
  const levelCls = LEVEL_STYLE[view.witness_level_label] || LEVEL_STYLE["Self-Declared"];

  // Privacy-safe PNG share: declaration ID, area, witness level, QR.
  // NEVER: candidate choice, email, address, phone, signature, tokens.
  const shareReceipt = async () => {
    try {
      const W = 720, H = 980;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, W, H);
      // header band
      ctx.fillStyle = "#1e3a8a"; ctx.fillRect(0, 0, W, 130);
      ctx.fillStyle = "#ffffff"; ctx.font = "bold 40px Arial";
      ctx.fillText("WitnessYourVote", 36, 62);
      ctx.fillStyle = "#bfdbfe"; ctx.font = "22px Arial";
      ctx.fillText(String(view.summary.election || "").slice(0, 48), 36, 100);
      // declaration
      ctx.fillStyle = "#64748b"; ctx.font = "bold 18px Arial";
      ctx.fillText("DECLARATION", 36, 180);
      ctx.fillStyle = "#0f172a"; ctx.font = "bold 44px 'Courier New', monospace";
      ctx.fillText(view.declaration_number, 36, 228);
      ctx.fillStyle = "#334155"; ctx.font = "24px Arial";
      ctx.fillText(`${view.summary.municipality || ""}${view.summary.ward ? " · " + view.summary.ward : ""}`.slice(0, 44), 36, 268);
      // status lines
      ctx.fillStyle = mutual ? "#1d4ed8" : witnessed ? "#047857" : "#475569";
      ctx.font = "bold 30px Arial";
      ctx.fillText(`${mutual ? "🔗 " : ""}${view.witness_level_label}`, 36, 330);
      ctx.fillStyle = "#0f172a"; ctx.font = "22px Arial";
      let y = 372;
      if (view.witness_count > 0) {
        ctx.fillText(mutual ? `${view.witness_count + 1} confirmed participants` : `${view.witness_count} witness${view.witness_count > 1 ? "es" : ""}`, 36, y);
        y += 34;
      }
      ctx.fillText(view.locked ? "🔒 Record Locked" : "Locks after witnessing", 36, y); y += 34;
      ctx.fillText(`✅ Integrity ${witnessed ? "Verified" : "Protected"}`, 36, y);
      // QR
      const qr = new Image();
      qr.crossOrigin = "anonymous";
      await new Promise((res, rej) => { qr.onload = res; qr.onerror = rej; qr.src = view.qr_url; });
      ctx.drawImage(qr, W - 300, 160, 260, 260);
      ctx.fillStyle = "#64748b"; ctx.font = "16px Arial";
      ctx.fillText("Scan to verify this record", W - 296, 444);
      // verify line
      ctx.fillStyle = "#0f172a"; ctx.font = "bold 24px Arial";
      ctx.fillText("VERIFY RECORD:", 36, 520);
      ctx.fillStyle = "#1e3a8a"; ctx.font = "20px 'Courier New', monospace";
      ctx.fillText(`${window.location.origin}/verify/${view.public_verification_id}`, 36, 552);
      // footer
      ctx.fillStyle = "#f1f5f9"; ctx.fillRect(0, H - 320, W, 320);
      ctx.fillStyle = "#475569"; ctx.font = "18px Arial";
      const foot = [
        "Voluntary WitnessYourVote declaration — not an official election result.",
        "The witness confirms this declaration was made and signed;",
        "they never see the official ballot.",
        "",
        "No name, candidate choice, email or address appears on this card.",
        "witnessyourvote — vote privately · witness independently · verify the count",
      ];
      foot.forEach((line, i) => ctx.fillText(line, 36, H - 270 + i * 30));
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], `wyv-receipt-${view.declaration_number}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My WitnessYourVote Receipt",
          text: "I made a voluntary WitnessYourVote declaration — and you can verify the record is intact.",
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = file.name; a.click();
        URL.revokeObjectURL(url);
        toast.success("Receipt image downloaded — share it anywhere");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not create the share image");
    }
  };

  return (
    <div className="wyv-container py-8 sm:py-12">
      {/* WALLET CARD — privacy-safe fields only (no email/address/phone/signature/tokens) */}
      <div className="max-w-sm mx-auto">
        <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-300 bg-white" data-testid="wallet-receipt-card">
          <div className="bg-primary text-white px-5 py-4 flex items-center gap-3">
            <img src="/wyv-icon-192.png" alt="WitnessYourVote" className="h-9 w-9 rounded-lg" />
            <div className="leading-tight">
              <p className="font-heading font-bold text-sm">WitnessYourVote</p>
              <p className="text-[11px] text-blue-100" data-testid="card-election">{view.summary.election}</p>
            </div>
            {view.is_test && <Badge variant="outline" className="ml-auto bg-amber-400/20 text-amber-100 border-amber-300/50 text-[9px]">TEST</Badge>}
          </div>
          <div className="px-5 py-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Declaration</p>
            <p className="font-mono font-bold text-xl text-foreground" data-testid="card-declaration-number">{view.declaration_number}</p>
            <p className="text-sm text-muted-foreground mt-0.5" data-testid="card-location">
              {view.summary.municipality}{view.summary.ward ? ` · ${view.summary.ward}` : ""}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <Badge variant="outline" className={`${levelCls} text-[11px]`} data-testid="card-witness-level">
                {mutual ? <Link2 className="h-3 w-3 mr-1" /> : <ShieldCheck className="h-3 w-3 mr-1" />}
                {view.witness_level_label}
              </Badge>
              {view.witness_count > 0 && (
                <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300 text-[11px]" data-testid="card-witness-count">
                  {mutual ? `${view.witness_count + 1} confirmed participants` : `${view.witness_count} witness${view.witness_count > 1 ? "es" : ""}`}
                </Badge>
              )}
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <p className={`flex items-center gap-2 ${view.locked ? "text-foreground" : "text-muted-foreground"}`} data-testid="card-locked">
                <Lock className="h-3.5 w-3.5" /> {view.locked ? "Record Locked" : "Locks after witnessing"}
              </p>
              <p className="flex items-center gap-2 text-emerald-800" data-testid="card-integrity">
                <CheckCircle2 className="h-3.5 w-3.5" /> Integrity {witnessed ? "Verified" : "Protected"}
              </p>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <img src={view.qr_url} alt="QR code — verify this record" className="h-28 w-28 rounded-lg border border-slate-300" data-testid="card-qr" />
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                Scan to check this record on the public verification page. It never reveals private information.
              </div>
            </div>
            <Button asChild className="w-full h-11 mt-4" data-testid="card-verify-button">
              <Link to={`/verify/${view.public_verification_id}`}><Fingerprint className="h-4 w-4 mr-2" /> VERIFY RECORD</Link>
            </Button>
            <Button variant="outline" className="w-full h-11 mt-2" onClick={shareReceipt} data-testid="card-share-button">
              <Share2 className="h-4 w-4 mr-2" /> Share My Receipt
            </Button>
            <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
              The shared image never includes your name, candidate choice, email or address.
            </p>
          </div>
          <div className="px-5 py-3 bg-secondary border-t border-slate-200">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Voluntary WitnessYourVote declaration — not an official election record.
            </p>
          </div>
        </div>

        {/* Add to Home Screen */}
        <Collapsible className="mt-5">
          <CollapsibleTrigger className="w-full" data-testid="a2hs-toggle">
            <div className="rounded-lg border border-slate-200 bg-white p-3.5 flex items-center gap-2.5 text-sm font-medium text-foreground hover:border-primary/40">
              <Smartphone className="h-4 w-4 text-accent" /> Save this receipt to your phone
              <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card className="mt-2">
              <CardContent className="p-5 text-sm text-muted-foreground space-y-3" data-testid="a2hs-instructions">
                <p className="font-semibold text-foreground">Add to Home Screen</p>
                <p><span className="font-medium text-foreground">iPhone/iPad (Safari):</span> tap the Share button, then “Add to Home Screen”.</p>
                <p><span className="font-medium text-foreground">Android (Chrome):</span> tap the ⋮ menu, then “Add to Home screen” (or “Install app”).</p>
                <p className="text-[11px]">
                  Bookmark this page's address — it is your private receipt link. Your email, address and signature
                  are never shown on this card and are never stored on your phone by this page.
                </p>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        <div className="flex justify-center mt-5">
          <Button asChild variant="ghost" size="sm" data-testid="card-back-to-declaration">
            <Link to={`/my-declaration/${ownerToken}`}>Back to my declaration page</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
